<template>
	<section class="profile-page">
		<header-page :title="$route.meta.title" />
		<div class="profile-content">
			<v-card class="profile-card" flat>
				<v-card-title class="profile-heading">
					<span class="profile-avatar" aria-hidden="true">{{ initial }}</span>
					<div class="profile-identity">
						<span class="profile-eyebrow">MI CUENTA</span>
						<h1>{{ form.userName || 'Mi perfil' }}</h1>
						<div class="profile-badges">
							<span class="profile-badge" :class="{ 'profile-badge--admin': isAccountant }">
								<v-icon small aria-hidden="true">{{ isAccountant ? 'verified_user' : 'person_outline' }}</v-icon>
								{{ isAccountant ? 'Contador administrador' : 'Auxiliar contable' }}
							</span>
							<span class="profile-badge profile-badge--mail">
								<v-icon small aria-hidden="true">mail_outline</v-icon>{{ form.email }}
							</span>
						</div>
					</div>
				</v-card-title>
				<v-card-text>
					<v-alert :value="!!error" type="error">{{ error }}</v-alert>
					<v-alert :value="!!message" type="success">{{ message }}</v-alert>
					<v-progress-linear v-if="loading" indeterminate />

					<template v-if="!loading && ready">
						<v-form ref="dataForm" @submit.prevent="saveData">
							<h2 class="profile-section-title">Datos personales</h2>
							<p class="profile-hint">Así te ve el resto de la organización.</p>
							<div class="profile-fields">
								<base-input :height="40" :hide-details="false" v-model="form.userName" label="Nombre" :rules="[required]" :disabled="saving" />
								<base-input :height="40" :hide-details="false" v-model="form.numberDocument" label="Número de documento" :rules="[required]" :disabled="saving" />
								<base-input :height="40" :hide-details="false" v-model="form.phone" label="Teléfono (opcional)" :disabled="saving" />
								<base-input :height="40" :hide-details="false" v-model="form.email" label="Correo" :disabled="true" />
							</div>
							<p class="profile-note">
								<v-icon small aria-hidden="true">lock_outline</v-icon>
								El correo es tu credencial de acceso y no se puede cambiar desde aquí.
							</p>
							<div class="profile-actions">
								<v-btn flat :disabled="saving || !dataChanged" @click="resetData">Descartar</v-btn>
								<v-btn type="submit" class="profile-save" color="secondary" :loading="savingData" :disabled="saving || !dataChanged">
									<v-icon small left aria-hidden="true">save</v-icon>Guardar cambios
								</v-btn>
							</div>
						</v-form>

						<v-divider class="profile-divider" />

						<v-form ref="passwordForm" @submit.prevent="savePassword">
							<h2 class="profile-section-title">Contraseña</h2>
							<p class="profile-hint">Al cambiarla, la próxima vez entrarás con la nueva.</p>
							<div class="profile-fields">
								<base-input
									:height="40" :hide-details="false" v-model="password.value" label="Nueva contraseña"
									:type="showPassword ? 'text' : 'password'" autocomplete="new-password"
									:rules="[required, minLength]" :disabled="saving"
								/>
								<base-input
									:height="40" :hide-details="false" v-model="password.repeat" label="Repetir contraseña"
									:type="showPassword ? 'text' : 'password'" autocomplete="new-password"
									:rules="[required, sameAsPassword]" :disabled="saving"
								/>
							</div>
							<v-checkbox v-model="showPassword" class="profile-show" hide-details label="Mostrar contraseña" :disabled="saving" />
							<div class="profile-actions">
								<v-btn type="submit" class="profile-save" color="secondary" :loading="savingPassword" :disabled="saving">
									<v-icon small left aria-hidden="true">vpn_key</v-icon>Cambiar contraseña
								</v-btn>
							</div>
						</v-form>
					</template>

					<v-btn v-if="!loading && !ready" flat @click="load">Reintentar</v-btn>
				</v-card-text>
			</v-card>
		</div>
	</section>
</template>

<script>
import BaseInput from '@/components/shared/inputs/base-input';

const MIN_PASSWORD = 6;

function emptyForm() {
	return { userName: '', numberDocument: '', phone: '', email: '' };
}

export default {
	name: 'page-profile',
	components: { BaseInput },
	data() {
		return {
			loading: false,
			ready: false,
			savingData: false,
			savingPassword: false,
			error: '',
			message: '',
			showPassword: false,
			isAccountant: false,
			auxiliaryId: null,
			form: emptyForm(),
			original: emptyForm(),
			password: { value: '', repeat: '' },
		};
	},
	created() { this.load(); },
	computed: {
		saving() { return this.savingData || this.savingPassword; },
		initial() { return (this.form.userName || '?').trim().slice(0, 1).toUpperCase(); },
		dataChanged() {
			return ['userName', 'numberDocument', 'phone']
				.some(field => (this.form[field] || '') !== (this.original[field] || ''));
		},
	},
	methods: {
		required(value) { return !!value || 'Campo obligatorio'; },
		minLength(value) {
			return (value || '').length >= MIN_PASSWORD || `Usa al menos ${MIN_PASSWORD} caracteres`;
		},
		sameAsPassword(value) {
			return value === this.password.value || 'Las contraseñas no coinciden';
		},
		report(error) {
			const response = error.response || error;
			this.error = (response.data && response.data.message) ||
				'No se pudo completar la operación. Intenta nuevamente.';
		},
		async load() {
			this.loading = true; this.ready = false; this.error = ''; this.message = '';
			try {
				const { data: session } = await this.$http.get('company-token');
				const auxiliary = session.auxiliary;
				if (!auxiliary || !auxiliary.id) {
					this.error = 'Tu usuario no está vinculado a una organización contable.';
					return;
				}
				this.auxiliaryId = auxiliary.id;
				this.isAccountant = [true, 1].includes(auxiliary.flagAccountant);
				this.form = {
					userName: auxiliary.userName || '',
					numberDocument: auxiliary.numberDocument || '',
					phone: auxiliary.phone || '',
					email: auxiliary.email || '',
				};
				this.original = { ...this.form };
				this.ready = true;
			} catch (error) { this.report(error); } finally { this.loading = false; }
		},
		resetData() {
			this.form = { ...this.original };
			this.error = ''; this.message = '';
		},
		syncSession() {
			const key = `${process.env.STORAGE_USER_KEY}::adminUser`;
			try {
				const stored = JSON.parse(localStorage.getItem(key));
				if (stored && stored.auxiliary) {
					stored.auxiliary.userName = this.form.userName;
					stored.auxiliary.numberDocument = this.form.numberDocument;
					stored.auxiliary.phone = this.form.phone;
					localStorage.setItem(key, JSON.stringify(stored));
					this.$bus.$emit('USER_INFO', JSON.stringify(stored));
				}
			} catch (error) { this.error = ''; }
		},
		async saveData() {
			if (this.saving || !this.$refs.dataForm.validate()) return;
			this.savingData = true; this.error = ''; this.message = '';
			try {
				await this.$http.patch(`auxiliary/${this.auxiliaryId}`, {
					userName: this.form.userName,
					numberDocument: this.form.numberDocument,
					phone: this.form.phone,
					email: this.form.email,
				});
				this.original = { ...this.form };
				this.syncSession();
				this.message = 'Tus datos quedaron actualizados.';
			} catch (error) { this.report(error); } finally { this.savingData = false; }
		},
		async savePassword() {
			if (this.saving || !this.$refs.passwordForm.validate()) return;
			this.savingPassword = true; this.error = ''; this.message = '';
			try {
				await this.$http.patch(`auxiliary/${this.auxiliaryId}`, {
					userName: this.original.userName,
					numberDocument: this.original.numberDocument,
					phone: this.original.phone,
					email: this.original.email,
					password: this.password.value,
				});
				this.password = { value: '', repeat: '' };
				this.showPassword = false;
				this.$refs.passwordForm.reset();
				this.message = 'Tu contraseña fue actualizada.';
			} catch (error) { this.report(error); } finally { this.savingPassword = false; }
		},
	},
};
</script>

<style lang="scss">
.profile-page {
	background: color(background-table);
	min-height: calc(100vh - 80px);
	color: color(text);
	font-family: font(AvenirRegular);
}
.profile-content { max-width: 900px; padding: 28px; margin: auto; }
.profile-card { border: 1px solid color(grey-divider); border-radius: 12px; overflow: hidden; }
.profile-heading {
	gap: 16px;
	padding: 24px;
	border-bottom: 1px solid color(grey-divider);
	align-items: center;
	h1 { background: transparent !important; color: color(black); font-family: font(AvenirBold); font-size: 24px; margin: 4px 0; padding: 0; }
}
.profile-avatar {
	align-items: center;
	background: color(secondary);
	border-radius: 50%;
	color: #fff;
	display: inline-flex;
	font-family: font(AvenirBold);
	font-size: 22px;
	height: 56px;
	justify-content: center;
	width: 56px;
}
.profile-identity { min-width: 0; }
.profile-eyebrow { color: color(secondary); font-size: 11px; letter-spacing: 1.5px; }
.profile-badges { display: flex; flex-wrap: wrap; gap: 8px; }
.profile-badge {
	align-items: center;
	background: color(blue-grey);
	border-radius: 12px;
	display: inline-flex;
	font-size: 12px;
	gap: 4px;
	padding: 2px 10px;
}
.profile-badge .v-icon { margin-right: 4px; }
.profile-badge--admin { color: color(secondary); }
.profile-badge--mail { overflow-wrap: anywhere; }
.profile-card .v-card__text { padding: 24px; }
.profile-card .v-btn { border-radius: 6px; text-transform: none; letter-spacing: normal; }
.profile-section-title { font-family: font(AvenirBold); font-size: 16px; margin: 0; }
.profile-hint { color: rgba(0, 0, 0, 0.6); font-size: 13px; margin: 2px 0 18px; }
.profile-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px 24px; }
.profile-note {
	align-items: center;
	color: rgba(0, 0, 0, 0.6);
	display: flex;
	font-size: 12px;
	gap: 6px;
	margin: 12px 0 0;
}
.profile-show { margin-top: 8px; }
.profile-page .profile-show .v-input--selection-controls__input .theme--light.v-icon { color: color(secondary) !important; }
.profile-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 20px; }
.profile-divider { margin: 32px 0 28px; }
@media (max-width: 700px) {
	.profile-content { padding: 16px; }
	.profile-fields { grid-template-columns: minmax(0, 1fr); }
}
</style>



// WEBPACK FOOTER //
// src/pages/profile/page-profile.vue